import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  DndContext, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import type { ChecklistSection, ItineraryDay, OutfitGroup } from '../../../../types';
import OutfitPieceList, { type ShownPiece, WHOLE_DAY_DROP_ID } from './OutfitPieceList';
import OutfitMomentPicker from './OutfitMomentPicker';
import { ChevronDown, Pencil, Trash2, Icon } from '../../../ui/Icon';

export type DayPiece = ShownPiece & { groupId: string | null };

interface Props {
  day: ItineraryDay;
  open: boolean;
  onToggle: () => void;
  pieces: DayPiece[];
  groups: OutfitGroup[];
  sections: ChecklistSection[];
  defaultSectionId: string | null;
  readOnly: boolean;
  onPick: (groupId: string | null, itemId: string) => Promise<void>;
  onCreate: (groupId: string | null, text: string, sectionId: string | null) => Promise<void>;
  onRemove: (pieceId: string) => void;
  /** Drops a piece into another moment of this day; null = the whole day. */
  onMove: (pieceId: string, groupId: string | null) => void;
  onCreateGroup: (title: string) => Promise<void>;
  onRenameGroup: (groupId: string, title: string) => void;
  onDeleteGroup: (groupId: string) => void;
}

/**
 * One day of planned clothes. ADR-0017: the day is directly "what I wear";
 * groups ("Beach", "Dinner") exist only if the user makes them.
 */
export default function OutfitDayCard({
  day, open, onToggle, pieces, groups, sections, defaultSectionId, readOnly,
  onPick, onCreate, onRemove, onMove, onCreateGroup, onRenameGroup, onDeleteGroup,
}: Props) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const dayItemIds = new Set(pieces.map(p => p.checklistItemId));
  // Nowhere to drag to until the day has a moment.
  const listProps = { sections, defaultSectionId, dayItemIds, readOnly, onRemove, draggable: !readOnly && groups.length > 0 };
  // Distance/delay so a click on a chip's × still removes it instead of starting a drag.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over) return;
    onMove(String(active.id), over.id === WHOLE_DAY_DROP_ID ? null : String(over.id));
  }

  function submitRename() {
    if (renaming?.title.trim()) onRenameGroup(renaming.id, renaming.title.trim());
    setRenaming(null);
  }

  return (
    <div className="card p-4 sm:p-5">
      <button onClick={onToggle} aria-expanded={open} className="w-full flex items-baseline gap-2 cursor-pointer text-left">
        <span className="font-mono text-[10px] font-medium tracking-[0.14em] uppercase text-slate-400 dark:text-slate-500">
          {t('itinerary.dayLabel', { n: day.dayNumber })}
        </span>
        <span className="text-xs text-slate-400 dark:text-slate-500">{format(new Date(day.date), 'd MMM')}</span>
        {day.title && <span className="text-xs text-slate-500 dark:text-slate-400 truncate">· {day.title}</span>}
        <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-slate-400 dark:text-slate-500">
          {t('packing.outfits.pieceCount', { count: pieces.length })}
        </span>
        <Icon as={ChevronDown} size={14} className={`self-center text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="mt-3 space-y-3">
            {groups.length > 0 && (
              <p className="-mb-1.5 font-mono text-[10px] font-medium tracking-[0.12em] uppercase text-slate-500 dark:text-slate-400">
                {t('packing.outfits.allDay')}
              </p>
            )}
            <OutfitPieceList
              {...listProps}
              dropId={WHOLE_DAY_DROP_ID}
              pieces={pieces.filter(p => p.groupId === null)}
              onPick={itemId => onPick(null, itemId)}
              onCreate={(text, sectionId) => onCreate(null, text, sectionId)}
            />

            {groups.map(group => (
              <div key={group.id} className="pt-3 border-t border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-1 mb-1.5">
                  {renaming?.id === group.id ? (
                    <input
                      autoFocus
                      value={renaming.title}
                      onChange={e => setRenaming({ id: group.id, title: e.target.value })}
                      onBlur={submitRename}
                      onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(null); }}
                      aria-label={t('packing.outfits.groupName')}
                      className="flex-1 bg-transparent border-b border-teal-400 text-[11px] font-semibold uppercase tracking-wide py-0.5 focus:outline-none"
                    />
                  ) : (
                    <p className="flex-1 font-mono text-[10px] font-medium tracking-[0.12em] uppercase text-slate-500 dark:text-slate-400">
                      {group.title}
                    </p>
                  )}
                  {!readOnly && (
                    <>
                      <button
                        onClick={() => setRenaming({ id: group.id, title: group.title })}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                        aria-label={t('packing.outfits.renameGroup')}
                      >
                        <Icon as={Pencil} size={11} />
                      </button>
                      <button
                        onClick={() => onDeleteGroup(group.id)}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/25 cursor-pointer"
                        aria-label={t('packing.outfits.deleteGroup')}
                        title={t('packing.outfits.deleteGroupHint')}
                      >
                        <Icon as={Trash2} size={11} />
                      </button>
                    </>
                  )}
                </div>
                <OutfitPieceList
                  {...listProps}
                  dropId={group.id}
                  pieces={pieces.filter(p => p.groupId === group.id)}
                  onPick={itemId => onPick(group.id, itemId)}
                  onCreate={(text, sectionId) => onCreate(group.id, text, sectionId)}
                />
              </div>
            ))}

            {!readOnly && (
              <OutfitMomentPicker taken={groups.map(g => g.title)} onCreate={onCreateGroup} />
            )}

            {readOnly && pieces.length === 0 && (
              <p className="text-xs text-slate-300 dark:text-slate-600">{t('packing.outfits.emptyDay')}</p>
            )}
          </div>
        </DndContext>
      )}
    </div>
  );
}
